import { combineReducers } from 'redux';
import types from './types';
import { IInitialState } from 'src/store/initialState';
import CartModel from 'src/model/cart.model';
import { IShippingMethod } from 'src/services/checkout/CheckoutService';

export interface ICheckoutError {
  hasFailed: boolean;
  message: string;
}

const INITIAL_IS_PENDING = false;
const INITIAL_ORDER_CONFIRMED = false;
const INITIAL_CREDIT_CARD_URL: string = null;
const INITIAL_CONFIRMATION_NUMBER = 0;
const INITIAL_CHECKOUT_CODE = 0;
const INITIAL_CART: CartModel = {
  delivery: 'Shipping',
  deliveryLocation: null,
  deliveryLocationCode: null,
  suppliedFromCode: null,
  subTotal: '0.00',
  itemCount: 0,
  items: {}
};
const INITIAL_SUBTOTAL = '0.00';
const INITIAL_TAX = '0.00';
const INITIAL_TOTAL = '0.00';
const INITIAL_DEFAULT_MOT: string = null;
const INITIAL_TERMS_OF_DELIVERY: string = null;
const INITIAL_SOFT_CREDIT_HOLD: boolean = false;
const INITIAL_ERROR: ICheckoutError = {
  hasFailed: false,
  message: ''
};
const INITIAL_PLACING_ORDER_PROGRESS = false;

const isPending = (
  state: boolean = INITIAL_IS_PENDING,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_IS_PENDING:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const orderConfirmed = (
  state: boolean = INITIAL_ORDER_CONFIRMED,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_ORDER_CONFIRMED:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const creditCardUrl = (
  state: string = INITIAL_CREDIT_CARD_URL,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_CREDIT_CARD_URL:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const pendingOrderNumber = (
  state: number = 0,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_PENDING_ORDER_NUMBER:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const confirmationNumber = (
  state: number = INITIAL_CONFIRMATION_NUMBER,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_CONFIRMATION_NUMBER:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const checkoutCode = (
  state: number = INITIAL_CHECKOUT_CODE,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_CHECKOUT_CODE:
      return payload;

    default:
      return state;
  }
};

const cart = (
  state: CartModel = INITIAL_CART,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_CART:
      return payload;

    default:
      return state;
  }
};

const subtotal = (
  state: string = INITIAL_SUBTOTAL,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_SUBTOTAL:
      return payload;

    default:
      return state;
  }
};

const shippingMethod = (
  state: IShippingMethod = { motcode: '', motdescription: '' },
  {
    type,
    payload
  }: {
    type: string;
    payload: IShippingMethod;
  }
) => {
  switch (type) {
    case types.SET_ORDER_SHIPPING_METHOD:
      return payload;

    default:
      return state;
  }
};

const tax = (
  state: string = INITIAL_TAX,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_TAX:
      return payload;

    default:
      return state;
  }
};

const total = (
  state: string = INITIAL_TOTAL,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_TOTAL:
      return payload;

    default:
      return state;
  }
};

const defaultMOT = (
  state: string = INITIAL_DEFAULT_MOT,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_DEFAULT_MOT:
      return payload;

    default:
      return state;
  }
};

const termsOfDelivery = (
  state: string = INITIAL_TERMS_OF_DELIVERY,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_TERMS_OF_DELIVERY:
      return payload;

    default:
      return state;
  }
};

const softCreditHold = (
  state: boolean = INITIAL_SOFT_CREDIT_HOLD,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_SOFT_CREDIT_HOLD:
      return payload;

    default:
      return state;
  }
};

const error = (
  state: ICheckoutError = INITIAL_ERROR,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.SET_ERROR:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

const placingOrderProgress = (
  state: boolean = INITIAL_PLACING_ORDER_PROGRESS,
  {
    type,
    payload
  }: {
    type: string;
    payload: any;
  }
) => {
  switch (type) {
    case types.PLACE_ORDER_PROGRESS:
      return payload;
      break;

    default:
      return state;
      break;
  }
};

export default combineReducers({
  isPending,
  orderConfirmed,
  shippingMethod,
  creditCardUrl,
  pendingOrderNumber,
  confirmationNumber,
  checkoutCode,
  cart,
  subtotal,
  tax,
  total,
  defaultMOT,
  termsOfDelivery,
  softCreditHold,
  error,
  placingOrderProgress
});
